import { handleError } from '@/services/handleError';

type ImageState = {
  loading: boolean;
  error: boolean;
  src: string | null;
};

type Subscriber = (state: ImageState) => void;

type PendingEntry = {
  promise: Promise<string>;
  controller: AbortController;
};

const imageCache = new Map<string, string>();
const pendingPromises = new Map<string, PendingEntry>();
const imageStates = new Map<string, ImageState>();
const subscribers = new Map<string, Set<Subscriber>>();

function getState(src: string): ImageState {
  const stored = imageStates.get(src);
  if (stored) return stored;

  if (imageCache.has(src)) {
    return { loading: false, error: false, src: imageCache.get(src)! };
  }

  return { loading: true, error: false, src: null };
}

function setState(src: string, patch: Partial<ImageState>) {
  const current = imageStates.get(src) ?? {
    loading: true,
    error: false,
    src: null
  };
  const next = { ...current, ...patch };
  imageStates.set(src, next);
  subscribers.get(src)?.forEach((cb) => cb(next));
}

function subscribe(src: string, cb: Subscriber): () => void {
  if (!subscribers.has(src)) subscribers.set(src, new Set());
  subscribers.get(src)!.add(cb);

  return () => {
    const subs = subscribers.get(src);
    if (!subs) return;

    subs.delete(cb);

    if (subs.size === 0) {
      // Финальное состояние (успех/ошибка) сохраняем для соблюдения
      // правила "одна загрузка за сессию" — даже после ухода всех подписчиков.
      // Чистим только промежуточное loading-состояние и in-flight запрос.
      const state = imageStates.get(src);
      if (state?.loading) {
        pendingPromises.get(src)?.controller.abort();
        pendingPromises.delete(src);
        imageStates.delete(src);
      }
      subscribers.delete(src);
    }
  };
}

async function loadImage(
  src: string,
  fetcher: (src: string, signal: AbortSignal) => Promise<string>,
  loadingFromParent: boolean
): Promise<void> {
  if (imageCache.has(src)) {
    setState(src, { loading: false, error: false, src: imageCache.get(src)! });
    return;
  }

  // Ошибка в рамках сессии уже зафиксирована — повторно не запрашиваем.
  if (imageStates.get(src)?.error) {
    setState(src, { loading: false, error: true, src: null });
    return;
  }

  const needsConversion =
    src.startsWith('https') && !/\.webp$/.test(src) && !loadingFromParent;

  if (!needsConversion) {
    setState(src, { loading: false, error: false, src });
    return;
  }

  if (pendingPromises.has(src)) {
    try {
      await pendingPromises.get(src)!.promise;
    } catch {
      // состояние уже выставил тот, кто создал промис
    }
    return;
  }

  setState(src, { loading: true, error: false, src: null });

  const controller = new AbortController();

  const promise = fetcher(src, controller.signal)
    .then((base64) => {
      const dataUrl = 'data:image/webp;base64,' + base64;
      imageCache.set(src, dataUrl);
      return dataUrl;
    })
    .finally(() => pendingPromises.delete(src));

  pendingPromises.set(src, { promise, controller });

  try {
    const resolved = await promise;
    setState(src, { loading: false, error: false, src: resolved });
  } catch (error) {
    // Отмена через AbortController при уходе всех подписчиков —
    // не считаем ошибкой: следующий подписчик должен иметь возможность
    // перезапустить запрос. imageStates уже очищено в subscribe cleanup.
    if (controller.signal.aborted) return;

    // Состояние ошибки фиксируем безусловно, чтобы не зависнуть в loading,
    // даже если error не Error-like и handleError сделает early return.
    setState(src, { loading: false, error: true, src: null });
    handleError(error, { notify: false });
  }
}

export const imageStore = { getState, subscribe, loadImage };
